import { useState } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import {
  createVisitPreSaleFeedbackEndpoint,
  PreSaleVisitFeedbackBody,
  deleteVisitPreSaleEndpoint,
  markAsDoneVisitPreSaleEndpoint,
  reassignSdrVisitPreSaleEndpoint,
} from '../services/dashboardService';
import { getProjectModelsEndpoint } from '../../investors/services/investorService';
import { getCancellationReasons } from '../../properties/services/propertyService';
import {
  getAvailableTimeProjectVisitsEndpoint,
  updateProjectVisitsEndpoint,
} from '../../investors/services/visitsInvestorService';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { VisitPreSaleState } from '../../../constants/visitsConstants';

export interface ProjectModelOption {
  id: string;
  name: string;
}

export interface AvailableTime {
  minutes: number;
  time: string;
  is_blocked?: boolean;
}

export const REASSIGN_SDR_STATUS_NOT_ALLOWED = 1113;
export const REASSIGN_SDR_NOT_ASSIGNABLE = 1114;
export const REASSIGN_SDR_VISIT_NOT_FOUND = 1102;

const REASSIGN_SDR_MESSAGES: Record<number, string> = {
  [REASSIGN_SDR_STATUS_NOT_ALLOWED]:
    'Esta visita ya no se puede reasignar porque su estado cambió.',
  [REASSIGN_SDR_NOT_ASSIGNABLE]:
    'No se pudo asignar ese SDR. Actualizamos la lista: elige uno de nuevo. Si sigue fallando, intenta más tarde.',
  [REASSIGN_SDR_VISIT_NOT_FOUND]:
    'No se encontró la visita. Vuelve a la lista de visitas para actualizarla.',
};

export type ReassignSdrResult =
  | { ok: true }
  | {
      ok: false;
      code?: number;
      message: string;
      visitStatus?: VisitPreSaleState;
    };

// Acciones del detalle de una visita/asesoría de preventa. Reutiliza los
// endpoints que ya usa la tabla del dashboard (mark-as-done y cancel).
const usePreSaleVisitActions = () => {
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [cancellationReasons, setCancellationReasons] = useState<string[]>([]);
  const [availableTimes, setAvailableTimes] = useState<AvailableTime[]>([]);

  const doMarkAsDone = async (visitId: string): Promise<number> => {
    const { status } = await callEndpoint(
      markAsDoneVisitPreSaleEndpoint(visitId, token),
    );
    return status;
  };

  // Tipologías (modelos) del proyecto de la visita, para el feedback.
  const doLoadProjectModels = async (
    projectId: string,
  ): Promise<ProjectModelOption[]> => {
    const { status, data } = await callEndpoint(
      getProjectModelsEndpoint(projectId, token),
    );
    const list = status === 200 ? fetchResponseAdapter(data).data : [];
    return Array.isArray(list)
      ? list.map((m: { id: string; name: string }) => ({
          id: m.id,
          name: m.name,
        }))
      : [];
  };

  // 201 = creado; 409 (código 1106) = ya registrado → se trata como éxito.
  const doSendFeedback = async (
    visitId: string,
    body: PreSaleVisitFeedbackBody,
  ): Promise<{ ok: boolean; message?: string }> => {
    const { status, data } = await callEndpoint(
      createVisitPreSaleFeedbackEndpoint(visitId, token, body),
    );
    if (status === 201 || status === 200 || status === 409) return { ok: true };
    const messages: Record<number, string> = {
      400: 'La tipología seleccionada no pertenece al proyecto de la visita.',
      404: 'La visita no existe o no está asignada a este Proper.',
    };
    return {
      ok: false,
      message:
        messages[status] ||
        data?.message ||
        'No se pudo guardar el feedback. Intenta de nuevo.',
    };
  };

  const doCancelVisit = async (
    visitId: string,
    reason: string,
    explainReason: string,
    visitStatus: string,
  ): Promise<number> => {
    const { status } = await callEndpoint(
      deleteVisitPreSaleEndpoint(
        visitId,
        token,
        reason,
        explainReason,
        visitStatus,
      ),
    );
    return status;
  };

  const doReassignSdr = async (
    visitId: string,
    sdrId: string,
  ): Promise<ReassignSdrResult> => {
    const { status, data } = await callEndpoint(
      reassignSdrVisitPreSaleEndpoint(visitId, sdrId, token),
    );
    if (status === 200) return { ok: true };
    const response = fetchResponseAdapter(data ?? {});
    return {
      ok: false,
      code: response.code,
      // 400 con código 0 = body inválido: `message` trae el detalle.
      message:
        REASSIGN_SDR_MESSAGES[response.code] ||
        (status === 400 && response.message) ||
        'No se pudo reasignar el SDR. Intenta de nuevo.',
      visitStatus: response.data?.status,
    };
  };

  const doLoadCancellationReasons = async () => {
    try {
      const { data } = await callEndpoint(getCancellationReasons(token));
      setCancellationReasons(Array.isArray(data.data) ? data.data : []);
    } catch {
      setCancellationReasons([]);
    }
  };

  const doLoadAvailableTimes = async (projectId: string, date: string) => {
    if (!date) {
      setAvailableTimes([]);
      return;
    }
    const { status, data } = await callEndpoint(
      getAvailableTimeProjectVisitsEndpoint(projectId, date, token),
    );
    setAvailableTimes(
      status === 200 && Array.isArray(fetchResponseAdapter(data).data)
        ? fetchResponseAdapter(data).data
        : [],
    );
  };

  const doRescheduleVisit = async (
    visitId: string,
    date: string,
    startTime: number,
    properId: string,
  ): Promise<number> => {
    const { status } = await callEndpoint(
      updateProjectVisitsEndpoint(
        visitId,
        { date, start_time: startTime, proper_id: properId },
        token,
      ),
    );
    return status;
  };

  return {
    availableTimes,
    doLoadAvailableTimes,
    doRescheduleVisit,
    cancellationReasons,
    doMarkAsDone,
    doLoadProjectModels,
    doSendFeedback,
    doCancelVisit,
    doReassignSdr,
    doLoadCancellationReasons,
  };
};

export default usePreSaleVisitActions;
